import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { adminApi, formatAud } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  Search, X, Plus, Trash2, Receipt, Send, Mail, Phone,
  Package, Car, Calendar, Check, ExternalLink, Printer, Eye,
  TrendingUp, DollarSign, FileStack, AlertTriangle, CheckCircle2, ShoppingCart,
} from "lucide-react";
import { toast } from "sonner";

const TIME_FILTERS = [
  { key: "today",      label: "Today" },
  { key: "yesterday",  label: "Yesterday" },
  { key: "this_week",  label: "This Week" },
  { key: "last_week",  label: "Last Week" },
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "all_time",   label: "All Time" },
];

const STATUS_META = {
  draft:    { label: "Draft",    bg: "#f5f7fb", color: "#64748b", border: "#dde3ef" },
  sent:     { label: "Sent",     bg: "#eff6ff", color: "#1e40af", border: "#93c5fd" },
  accepted: { label: "Accepted", bg: "#f0fdf4", color: "#15803d", border: "#86efac" },
  rejected: { label: "Rejected", bg: "#fef2f2", color: "#991b1b", border: "#fca5a5" },
  expired:  { label: "Expired",  bg: "#fffbeb", color: "#92400e", border: "#fcd34d" },
};

const partsSummary = (items) => {
  if (!items?.length) return "";
  const first = items[0];
  const vehicle = [first.vehicle_year, first.vehicle_make, first.vehicle_model].filter(Boolean).join(" ");
  const label = vehicle ? `${first.name} — ${vehicle}` : first.name;
  return items.length > 1 ? `${label} +${items.length - 1} more` : label;
};

const emptyItem = () => ({
  source: "loose", product_id: null, sku: "", name: "",
  vehicle_make: "", vehicle_model: "", vehicle_year: "",
  stock_no: "", price: 0, notes: "",
});

export default function AdminPriceQuotes() {
  const location = useLocation();
  const [items,    setItems]    = useState([]);
  const [total,    setTotal]    = useState(0);
  const [stats,    setStats]    = useState({ total_quoted: 0, count: 0, conversion_rate: 0 });
  const [status,   setStatus]   = useState("");
  const [q,        setQ]        = useState("");
  const [rangeKey, setRangeKey] = useState("today");
  const [loading,  setLoading]  = useState(true);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // draft id being edited, if any
  const [viewing,  setViewing]  = useState(null);    // a sent/accepted quote being viewed read-only
  const [prefillState, setPrefillState] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { range_key: rangeKey };
      if (status) params.status = status;
      if (q.trim()) params.q = q.trim();
      const { data } = await adminApi.get("/admin/price-quotes", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setStats(data.stats || { total_quoted: 0, count: 0, conversion_rate: 0 });
    } catch {
      toast.error("Failed to load quotes");
    } finally { setLoading(false); }
  }, [status, q, rangeKey]);

  useEffect(() => { load(); }, [load]);

  // If we arrived here via "Create Quote" from a Quote Request, open the
  // builder pre-filled straight away.
  useEffect(() => {
    if (location.state?.prefill) {
      setPrefillState(location.state.prefill);
      setBuilderOpen(true);
      window.history.replaceState({}, document.title); // clear so refresh doesn't re-trigger
    }
  }, [location.state]);

  const openNew = () => { setPrefillState(null); setEditingId(null); setBuilderOpen(true); };
  const openEdit = (quote) => { setPrefillState(quote); setEditingId(quote.id); setBuilderOpen(true); };

  const deleteQuote = async (id) => {
    if (!window.confirm("Delete this draft quote? This can't be undone.")) return;
    try {
      await adminApi.delete(`/admin/price-quotes/${id}`);
      toast.success("Draft deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete this quote");
    }
  };

  const printQuote = async (id) => {
    const win = window.open("", "_blank");
    if (!win) { toast.error("Popup blocked — please allow popups for this site"); return; }
    try {
      const { data: html } = await adminApi.get(`/admin/price-quotes/${id}/print`);
      win.document.write(html);
      win.document.close();
    } catch {
      win.close();
      toast.error("Could not load quote for printing");
    }
  };

  const statusCounts = {};
  items.forEach(i => { statusCounts[i.status] = (statusCounts[i.status] || 0) + 1; });

  return (
    <div className="space-y-5" data-testid="admin-price-quotes-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-[28px] font-bold" style={{ color: "#0f172a" }}>
            Quotes <span className="text-[18px] font-medium" style={{ color: "#94a3b8" }}>({total})</span>
          </h1>
          <p className="text-[12px] mt-0.5" style={{ color: "#64748b" }}>
            Build and send price quotes to customers — from inventory or manually entered parts
          </p>
        </div>
        <Button onClick={openNew} className="rounded-xl text-white font-semibold text-[13px]"
          style={{ background: "#1e5bb8" }} data-testid="new-quote-btn">
          <Plus className="h-4 w-4 mr-1" /> New Quote
        </Button>
      </div>

      {/* Stats — over the current time/status/search filter, not just this page */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-4 shadow-sm flex items-center gap-3" style={{ border: "1px solid #dde3ef" }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#eff6ff" }}>
            <DollarSign className="h-5 w-5" style={{ color: "#1e5bb8" }} />
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>Total Quoted</div>
            <div className="font-heading text-[18px] font-bold" style={{ color: "#0f172a" }}>{formatAud(stats.total_quoted)}</div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-sm flex items-center gap-3" style={{ border: "1px solid #dde3ef" }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#f0fdf4" }}>
            <FileStack className="h-5 w-5" style={{ color: "#16a34a" }} />
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>Quotes Made</div>
            <div className="font-heading text-[18px] font-bold" style={{ color: "#0f172a" }}>{stats.count}</div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-sm flex items-center gap-3" style={{ border: "1px solid #dde3ef" }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#fffbeb" }}>
            <TrendingUp className="h-5 w-5" style={{ color: "#d97706" }} />
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>Conversion</div>
            <div className="font-heading text-[18px] font-bold" style={{ color: "#0f172a" }}>{stats.conversion_rate}%</div>
          </div>
        </div>
      </div>

      {/* Time filter */}
      <div className="flex items-center gap-1 flex-wrap bg-white rounded-2xl p-1.5 shadow-sm" style={{ border: "1px solid #dde3ef" }} data-testid="price-quotes-time-filter">
        {TIME_FILTERS.map(({ key, label }) => (
          <button key={key} onClick={() => setRangeKey(key)}
            className="px-3 py-1.5 rounded-xl text-[12px] font-semibold transition-colors"
            style={{ background: rangeKey === key ? "#1e5bb8" : "transparent", color: rangeKey === key ? "#fff" : "#64748b" }}>
            {label}
          </button>
        ))}
      </div>

      {/* Search + status filter */}
      <div className="bg-white rounded-2xl p-4 shadow-sm flex items-center gap-3 flex-wrap" style={{ border: "1px solid #dde3ef" }}>
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "#94a3b8" }} />
          <Input value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search quote #, customer name or email…"
            className="pl-9 rounded-xl h-10 text-[13px]"
            style={{ background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a" }} />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <button onClick={() => setStatus("")}
            className="px-3 py-1.5 rounded-lg text-[12px] font-semibold"
            style={{ background: status === "" ? "#0b1a3b" : "#f5f7fb", color: status === "" ? "#fff" : "#64748b" }}>
            All
          </button>
          {Object.entries(STATUS_META).map(([key, meta]) => (
            <button key={key} onClick={() => setStatus(key)}
              className="px-3 py-1.5 rounded-lg text-[12px] font-semibold"
              style={{ background: status === key ? meta.bg : "#f5f7fb", color: status === key ? meta.color : "#64748b", border: status === key ? `1px solid ${meta.border}` : "1px solid transparent" }}>
              {meta.label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="bg-white rounded-2xl p-16 text-center shadow-sm" style={{ border: "1px solid #dde3ef" }}>
          <div className="w-8 h-8 border-2 rounded-full animate-spin mx-auto" style={{ borderColor: "#1e5bb8", borderTopColor: "transparent" }} />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl p-16 text-center shadow-sm" style={{ border: "1px solid #dde3ef" }}>
          <Receipt className="h-10 w-10 mx-auto mb-3" style={{ color: "#cbd5e1" }} />
          <div className="text-[12px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>No quotes yet</div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm" style={{ border: "1px solid #dde3ef" }}>
          <table className="w-full text-sm">
            <thead style={{ background: "#f8fafc", borderBottom: "1px solid #eef2f8" }}>
              <tr className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>
                <th className="text-left px-4 py-3">Quote #</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Part / Vehicle</th>
                <th className="text-right px-4 py-3">Total</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Date</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => {
                const meta = STATUS_META[it.status] || STATUS_META.draft;
                return (
                  <tr key={it.id}
                    onClick={() => it.status === "draft" ? openEdit(it) : setViewing(it)}
                    className="cursor-pointer hover:brightness-95 transition-all"
                    style={{ background: meta.bg, borderBottom: idx < items.length - 1 ? "1px solid #ffffff" : "none" }}
                    data-testid={`price-quote-row-${it.quote_number}`}>
                    <td className="px-4 py-3 font-heading font-bold" style={{ color: "#0f172a" }}>{it.quote_number}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium" style={{ color: "#0f172a" }}>{it.customer_name}</div>
                      <div className="text-[11px]" style={{ color: "#64748b" }}>{it.customer_email}</div>
                    </td>
                    <td className="px-4 py-3 text-[12px] max-w-[220px] truncate" style={{ color: "#334155" }} title={partsSummary(it.items)}>
                      {partsSummary(it.items)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-bold" style={{ color: "#0f172a" }}>{formatAud(it.subtotal)}</td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded-lg"
                        style={{ background: "#fff", color: meta.color, border: `1px solid ${meta.border}` }}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-[12px]" style={{ color: "#64748b" }}>
                      {new Date(it.created_at).toLocaleDateString("en-AU")}
                    </td>
                    <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => it.status === "draft" ? openEdit(it) : setViewing(it)}
                          className="h-7 w-7 flex items-center justify-center rounded-md" title="View"
                          style={{ background: "rgba(0,0,0,0.06)", color: "#0f172a" }}
                          data-testid={`price-quote-view-${it.quote_number}`}>
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                        {it.status !== "draft" && (
                          <button onClick={() => printQuote(it.id)}
                            className="h-7 w-7 flex items-center justify-center rounded-md" title="Print"
                            style={{ background: "rgba(0,0,0,0.06)", color: "#0f172a" }}>
                            <Printer className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {it.status === "draft" && (
                          <button onClick={() => deleteQuote(it.id)}
                            className="h-7 w-7 flex items-center justify-center rounded-md" title="Delete draft"
                            style={{ background: "rgba(153,27,27,0.1)", color: "#991b1b" }}
                            data-testid={`price-quote-delete-${it.quote_number}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {builderOpen && (
        <QuoteBuilder
          initial={prefillState}
          editingId={editingId}
          onClose={() => setBuilderOpen(false)}
          onSaved={() => { setBuilderOpen(false); load(); }}
        />
      )}

      {viewing && (
        <QuoteViewer quote={viewing} onClose={() => setViewing(null)} onChanged={load} />
      )}
    </div>
  );
}

/* ── Builder — new or draft-edit. Add items from inventory search or as
   loose/manual entries. ─────────────────────────────────────────────── */
function QuoteBuilder({ initial, editingId, onClose, onSaved }) {
  const [customerName,  setCustomerName]  = useState(initial?.customer_name || "");
  const [customerEmail, setCustomerEmail] = useState(initial?.customer_email || "");
  const [customerPhone, setCustomerPhone] = useState(initial?.customer_phone || "");
  const [notes,         setNotes]         = useState(initial?.notes || "");
  const [validUntil,    setValidUntil]    = useState(initial?.valid_until ? initial.valid_until.slice(0, 10) : "");
  const [quoteItems,    setQuoteItems]    = useState(initial?.items?.length ? initial.items : [emptyItem()]);
  const [requestId]     = useState(initial?.request_id || null);
  const [saving,        setSaving]        = useState(false);

  const [invSearch, setInvSearch] = useState("");
  const [invResults, setInvResults] = useState([]);
  const [invOpenIdx, setInvOpenIdx] = useState(null);

  const subtotal = quoteItems.reduce((s, i) => s + (Number(i.price) || 0), 0);

  useEffect(() => {
    if (!invSearch.trim() || invOpenIdx === null) { setInvResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await adminApi.get("/admin/products", { params: { q: invSearch.trim(), page_size: 8 } });
        setInvResults(data.items || []);
      } catch { setInvResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [invSearch, invOpenIdx]);

  const updateItem = (idx, patch) => {
    setQuoteItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  };
  const removeItem = (idx) => setQuoteItems(prev => prev.filter((_, i) => i !== idx));
  const addLooseItem = () => setQuoteItems(prev => [...prev, emptyItem()]);

  const pickInventoryItem = (idx, product) => {
    updateItem(idx, {
      source: "inventory",
      product_id: product.id,
      sku: product.sku,
      name: product.name,
      vehicle_make: product.vehicle_make || "",
      vehicle_model: product.vehicle_model || "",
      vehicle_year: product.vehicle_year || "",
      stock_no: product.stock_no || "",
      price: product.price || 0,
    });
    setInvOpenIdx(null);
    setInvSearch("");
    setInvResults([]);
  };

  const buildPayload = () => ({
    customer_name: customerName,
    customer_email: customerEmail,
    customer_phone: customerPhone,
    items: quoteItems.map(({ source, product_id, sku, name, vehicle_make, vehicle_model, vehicle_year, stock_no, price, notes }) => ({
      source, product_id, sku, name, vehicle_make, vehicle_model, vehicle_year, stock_no, price: Number(price) || 0, notes,
    })),
    notes,
    valid_until: validUntil ? new Date(validUntil).toISOString() : null,
    request_id: requestId,
  });

  const validate = () => {
    if (!customerName.trim() || !customerEmail.trim()) { toast.error("Customer name and email are required"); return false; }
    if (quoteItems.length === 0 || quoteItems.some(i => !i.name?.trim())) { toast.error("Every item needs a name"); return false; }
    return true;
  };

  const saveDraft = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (editingId) {
        await adminApi.put(`/admin/price-quotes/${editingId}`, buildPayload());
      } else {
        await adminApi.post("/admin/price-quotes", buildPayload());
      }
      toast.success("Draft saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save quote");
    } finally { setSaving(false); }
  };

  const saveAndSend = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      let id = editingId;
      if (id) {
        await adminApi.put(`/admin/price-quotes/${id}`, buildPayload());
      } else {
        const { data } = await adminApi.post("/admin/price-quotes", buildPayload());
        id = data.id;
      }
      await adminApi.post(`/admin/price-quotes/${id}/send`);
      toast.success("Quote sent to customer");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send quote");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" style={{ border: "1px solid #dde3ef" }} onClick={e => e.stopPropagation()}>

        <div className="p-5 flex items-center justify-between sticky top-0 bg-white z-10" style={{ borderBottom: "1px solid #eef2f8" }}>
          <div className="font-heading text-xl font-bold" style={{ color: "#0f172a" }}>
            {editingId ? "Edit Quote" : "New Quote"}
          </div>
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center rounded-lg" style={{ color: "#94a3b8" }}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Customer */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#94a3b8" }}>Customer</div>
            <div className="grid grid-cols-2 gap-3">
              <Input value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Name"
                className="rounded-xl h-10 text-[13px]" style={{ borderColor: "#dde3ef" }} />
              <Input value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} placeholder="Phone (optional)"
                className="rounded-xl h-10 text-[13px]" style={{ borderColor: "#dde3ef" }} />
              <Input value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} placeholder="Email" type="email"
                className="rounded-xl h-10 text-[13px] col-span-2" style={{ borderColor: "#dde3ef" }} />
            </div>
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>Items</div>
              <button onClick={addLooseItem} className="text-[11px] font-semibold flex items-center gap-1" style={{ color: "#1e5bb8" }}>
                <Plus className="h-3 w-3" /> Add item
              </button>
            </div>
            <div className="space-y-3">
              {quoteItems.map((item, idx) => (
                <div key={idx} className="p-3 rounded-xl relative" style={{ background: "#f8fafc", border: "1px solid #eef2f8" }}>
                  <button onClick={() => removeItem(idx)} className="absolute top-2 right-2 h-6 w-6 flex items-center justify-center rounded-lg" style={{ color: "#94a3b8" }}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>

                  <div className="flex items-center gap-2 mb-2">
                    {item.source === "inventory" ? (
                      <span className="text-[10px] font-mono font-bold uppercase px-2 py-0.5 rounded-lg" style={{ background: "#eff6ff", color: "#1e40af" }}>
                        <Package className="h-2.5 w-2.5 inline mr-1" />Inventory
                      </span>
                    ) : (
                      <span className="text-[10px] font-mono font-bold uppercase px-2 py-0.5 rounded-lg" style={{ background: "#f0fdf4", color: "#15803d" }}>
                        Loose Part
                      </span>
                    )}
                    <div className="relative flex-1">
                      <button onClick={() => setInvOpenIdx(invOpenIdx === idx ? null : idx)}
                        className="text-[11px] font-semibold flex items-center gap-1" style={{ color: "#1e5bb8" }}>
                        <Search className="h-3 w-3" /> Search inventory
                      </button>
                      {invOpenIdx === idx && (
                        <div className="absolute z-20 mt-1 w-72 bg-white rounded-xl shadow-lg p-2" style={{ border: "1px solid #dde3ef" }}>
                          <Input autoFocus value={invSearch} onChange={e => setInvSearch(e.target.value)}
                            placeholder="Part name, SKU, model…" className="h-9 text-[12px] rounded-lg mb-1" />
                          {invResults.map(p => (
                            <button key={p.id} onClick={() => pickInventoryItem(idx, p)}
                              className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-[#f5f7fb] text-[12px]">
                              <div className="font-medium" style={{ color: "#0f172a" }}>{p.name}</div>
                              <div style={{ color: "#94a3b8" }}>{[p.vehicle_year, p.vehicle_make, p.vehicle_model].filter(Boolean).join(" ")} · {formatAud(p.price)}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <Input value={item.name} onChange={e => updateItem(idx, { name: e.target.value })} placeholder="Part name"
                      className="h-9 text-[12px] rounded-lg col-span-2" style={{ borderColor: "#dde3ef" }} />
                    <Input value={item.vehicle_make} onChange={e => updateItem(idx, { vehicle_make: e.target.value })} placeholder="Make"
                      className="h-9 text-[12px] rounded-lg" style={{ borderColor: "#dde3ef" }} />
                    <Input value={item.vehicle_model} onChange={e => updateItem(idx, { vehicle_model: e.target.value })} placeholder="Model"
                      className="h-9 text-[12px] rounded-lg" style={{ borderColor: "#dde3ef" }} />
                    <Input value={item.vehicle_year} onChange={e => updateItem(idx, { vehicle_year: e.target.value })} placeholder="Year"
                      className="h-9 text-[12px] rounded-lg" style={{ borderColor: "#dde3ef" }} />
                    <Input value={item.stock_no} onChange={e => updateItem(idx, { stock_no: e.target.value })} placeholder="Vehicle stock no."
                      className="h-9 text-[12px] rounded-lg" style={{ borderColor: "#dde3ef" }} />
                    <Input value={item.price} onChange={e => updateItem(idx, { price: e.target.value })} placeholder="Price" type="number" step="0.01"
                      className="h-9 text-[12px] rounded-lg font-mono col-span-2" style={{ borderColor: "#dde3ef" }} />
                    <Input value={item.notes} onChange={e => updateItem(idx, { notes: e.target.value })} placeholder="Notes (optional)"
                      className="h-9 text-[12px] rounded-lg col-span-2" style={{ borderColor: "#dde3ef" }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-2">
              <div className="text-[15px] font-heading font-bold" style={{ color: "#0f172a" }}>
                Total: {formatAud(subtotal)}
              </div>
            </div>
          </div>

          {/* Message + validity */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <div className="text-[10px] font-mono uppercase tracking-widest mb-1.5" style={{ color: "#94a3b8" }}>Message to customer (optional)</div>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
                className="w-full px-3 py-2 rounded-xl text-[13px] outline-none resize-none" style={{ border: "1px solid #dde3ef" }} />
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest mb-1.5" style={{ color: "#94a3b8" }}>Valid until (optional)</div>
              <Input type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)}
                className="h-10 rounded-xl text-[13px]" style={{ borderColor: "#dde3ef" }} />
            </div>
          </div>
        </div>

        <div className="p-5 flex gap-2 sticky bottom-0 bg-white" style={{ borderTop: "1px solid #eef2f8" }}>
          <button onClick={saveDraft} disabled={saving}
            className="flex-1 h-11 rounded-xl text-[13px] font-semibold"
            style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#475569" }}>
            {saving ? "Saving…" : "Save Draft"}
          </button>
          <button onClick={saveAndSend} disabled={saving}
            className="flex-1 h-11 rounded-xl text-white text-[13px] font-bold uppercase tracking-wide flex items-center justify-center gap-2"
            style={{ background: "#e31e24" }} data-testid="send-quote-btn">
            <Send className="h-4 w-4" /> {saving ? "Sending…" : "Save & Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Read-only viewer for a sent/accepted/rejected/expired quote ────── */
function QuoteViewer({ quote: initialQuote, onClose, onChanged }) {
  const [quote,     setQuote]     = useState(initialQuote);
  const [readiness, setReadiness] = useState(null);
  const [loadingReadiness, setLoadingReadiness] = useState(true);
  const [matchingIdx, setMatchingIdx] = useState(null);
  const [matchSearch, setMatchSearch] = useState("");
  const [matchResults, setMatchResults] = useState([]);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const meta = STATUS_META[quote.status] || STATUS_META.draft;

  const loadReadiness = async (q = quote) => {
    setLoadingReadiness(true);
    try {
      const { data } = await adminApi.get(`/admin/price-quotes/${q.id}/order-readiness`);
      setReadiness(data);
    } catch {
      toast.error("Could not check current stock status");
    } finally { setLoadingReadiness(false); }
  };

  useEffect(() => { loadReadiness(initialQuote); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (matchingIdx === null || !matchSearch.trim()) { setMatchResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await adminApi.get("/admin/products", { params: { q: matchSearch.trim(), page_size: 8 } });
        setMatchResults(data.items || []);
      } catch { setMatchResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [matchSearch, matchingIdx]);

  const applyMatch = async (idx, product) => {
    try {
      const { data } = await adminApi.put(`/admin/price-quotes/${quote.id}/items/${idx}/match`, { product_id: product.id });
      setQuote(data);
      setMatchingIdx(null); setMatchSearch(""); setMatchResults([]);
      toast.success("Item matched to inventory");
      loadReadiness(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not match this item");
    }
  };

  const [showOrderForm, setShowOrderForm] = useState(false);

  const createOrder = async (orderDetails) => {
    setCreatingOrder(true);
    try {
      const payload = {
        source: "phone",
        customer_name: quote.customer_name,
        customer_email: quote.customer_email,
        customer_phone: quote.customer_phone || "",
        items: quote.items.map(it => ({
          product_id: it.product_id, sku: it.sku || it.product_id, name: it.name, price: it.price, qty: 1,
        })),
        payment_method: orderDetails.paymentMethod,
        status: "paid",
        fulfillment_type: orderDetails.fulfillmentType,
        shipping: orderDetails.fulfillmentType === "delivery" ? Number(orderDetails.shippingCost) || 0 : 0,
        shipping_address: orderDetails.fulfillmentType === "delivery" ? orderDetails.address : null,
        generate_payment_link: orderDetails.paymentMethod === "stripe_link",
        notes: `Created from quote ${quote.quote_number}`,
      };
      const { data } = await adminApi.post("/admin/orders/manual", payload);
      const createdOrder = data.order || data;
      await adminApi.post(`/admin/price-quotes/${quote.id}/link-order`, {
        order_id: createdOrder.id, order_number: createdOrder.order_number,
      });
      toast.success(`Order ${createdOrder.order_number} created`);
      setShowOrderForm(false);
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create order");
    } finally { setCreatingOrder(false); }
  };

  const updateStatus = async (newStatus) => {
    setStatusUpdating(true);
    try {
      await adminApi.put(`/admin/price-quotes/${quote.id}/status`, { status: newStatus });
      toast.success(`Marked ${newStatus}`);
      setQuote(prev => ({ ...prev, status: newStatus }));
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update status");
    } finally { setStatusUpdating(false); }
  };

  const printQuote = async () => {
    const win = window.open("", "_blank");
    if (!win) { toast.error("Popup blocked — please allow popups for this site"); return; }
    try {
      const { data: html } = await adminApi.get(`/admin/price-quotes/${quote.id}/print`);
      win.document.write(html);
      win.document.close();
    } catch {
      win.close();
      toast.error("Could not load quote for printing");
    }
  };

  const itemState = (idx) => readiness?.items?.find(r => r.index === idx);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl" style={{ border: "1px solid #dde3ef" }} onClick={e => e.stopPropagation()}>
        <div className="p-5 flex items-start justify-between sticky top-0 bg-white z-10" style={{ borderBottom: "1px solid #eef2f8" }}>
          <div>
            <div className="font-heading text-xl font-bold" style={{ color: "#0f172a" }}>{quote.quote_number}</div>
            <div className="flex items-center gap-1 text-[11px] mt-1" style={{ color: "#94a3b8" }}>
              <Calendar className="h-3 w-3" /> {new Date(quote.created_at).toLocaleString("en-AU")}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-mono font-bold uppercase tracking-widest px-2.5 py-1 rounded-lg"
              style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }}>
              {meta.label}
            </span>
            <button onClick={onClose} className="h-8 w-8 flex items-center justify-center rounded-lg" style={{ color: "#94a3b8" }}>
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-4 text-sm">
          <div className="p-3 rounded-xl" style={{ background: "#f8fafc", border: "1px solid #eef2f8" }}>
            <div className="font-semibold text-[14px]" style={{ color: "#0f172a" }}>{quote.customer_name}</div>
            <a href={`mailto:${quote.customer_email}`} className="flex items-center gap-1.5 text-[12px] mt-1 hover:underline" style={{ color: "#1e5bb8" }}>
              <Mail className="h-3 w-3" /> {quote.customer_email}
            </a>
            {quote.customer_phone && (
              <a href={`tel:${quote.customer_phone}`} className="flex items-center gap-1.5 text-[12px] mt-1 hover:underline" style={{ color: "#1e5bb8" }}>
                <Phone className="h-3 w-3" /> {quote.customer_phone}
              </a>
            )}
          </div>

          {/* Items, each with live stock-readiness state */}
          <div className="space-y-2">
            {quote.items.map((it, i) => {
              const state = itemState(i);
              return (
                <div key={i} className="rounded-xl overflow-hidden" style={{ border: "1px solid #eef2f8" }}>
                  <div className="flex items-center justify-between gap-2 p-2.5" style={{ background: "#f8fafc" }}>
                    <div className="min-w-0">
                      <div className="text-[13px] font-medium truncate" style={{ color: "#0f172a" }}>{it.name}</div>
                      <div className="text-[11px]" style={{ color: "#94a3b8" }}>
                        {[it.vehicle_year, it.vehicle_make, it.vehicle_model].filter(Boolean).join(" ")}
                        {it.stock_no ? ` · Stock #${it.stock_no}` : ""}
                      </div>
                    </div>
                    <div className="text-[13px] font-mono font-bold shrink-0" style={{ color: "#0f172a" }}>{formatAud(it.price)}</div>
                  </div>

                  {!loadingReadiness && state && state.state !== "ok" && (
                    <div className="p-2.5" style={{ background: state.state === "sold" ? "#fef2f2" : "#fffbeb" }}>
                      <div className="flex items-center gap-1.5 text-[12px] font-semibold" style={{ color: state.state === "sold" ? "#991b1b" : "#92400e" }}>
                        <AlertTriangle className="h-3.5 w-3.5" />
                        {state.state === "sold" ? "This part has already been sold" : "Not linked to inventory yet"}
                      </div>
                      <div className="text-[11px] mt-0.5" style={{ color: "#64748b" }}>
                        {state.state === "sold" ? "Pick a different part from inventory to continue." : "This item needs to be matched to a real inventory part before an order can be created."}
                      </div>
                      {matchingIdx === i ? (
                        <div className="relative mt-2">
                          <Input autoFocus value={matchSearch} onChange={e => setMatchSearch(e.target.value)}
                            placeholder="Search inventory…" className="h-9 text-[12px] rounded-lg" />
                          {matchResults.length > 0 && (
                            <div className="mt-1 bg-white rounded-xl shadow-lg p-1.5" style={{ border: "1px solid #dde3ef" }}>
                              {matchResults.map(p => (
                                <button key={p.id} onClick={() => applyMatch(i, p)}
                                  className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-[#f5f7fb] text-[12px]">
                                  <div className="font-medium" style={{ color: "#0f172a" }}>{p.name}</div>
                                  <div style={{ color: "#94a3b8" }}>{[p.vehicle_year, p.vehicle_make, p.vehicle_model].filter(Boolean).join(" ")} · {formatAud(p.price)}</div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <button onClick={() => setMatchingIdx(i)}
                          className="mt-2 text-[11px] font-semibold flex items-center gap-1" style={{ color: "#1e5bb8" }}>
                          <Search className="h-3 w-3" /> {state.state === "sold" ? "Select other part from inventory" : "Match to inventory"}
                        </button>
                      )}
                    </div>
                  )}
                  {!loadingReadiness && state?.state === "ok" && (
                    <div className="px-2.5 py-1.5 flex items-center gap-1.5 text-[11px] font-semibold" style={{ background: "#f0fdf4", color: "#15803d" }}>
                      <CheckCircle2 className="h-3 w-3" /> In stock
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex justify-between items-center pt-2" style={{ borderTop: "1px solid #eef2f8" }}>
            <span className="font-heading font-bold text-[15px]" style={{ color: "#0f172a" }}>Total</span>
            <span className="font-mono font-bold text-[18px]" style={{ color: "#0f172a" }}>{formatAud(quote.subtotal)}</span>
          </div>
          {quote.notes && (
            <div className="text-[13px] whitespace-pre-wrap p-3 rounded-xl" style={{ color: "#374151", background: "#f8fafc" }}>{quote.notes}</div>
          )}
          {quote.sent_at && (
            <div className="text-[11px]" style={{ color: "#94a3b8" }}>Sent {new Date(quote.sent_at).toLocaleString("en-AU")}</div>
          )}
          {quote.order_number && (
            <div className="p-3 rounded-xl flex items-center gap-2" style={{ background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
              <ShoppingCart className="h-4 w-4" style={{ color: "#15803d" }} />
              <span className="text-[13px] font-semibold" style={{ color: "#15803d" }}>Order {quote.order_number} created from this quote</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-5 space-y-2 sticky bottom-0 bg-white rounded-b-2xl" style={{ borderTop: "1px solid #eef2f8" }}>
          {!quote.order_number && (
            <button onClick={() => setShowOrderForm(true)} disabled={loadingReadiness || !readiness?.ready}
              className="w-full h-11 rounded-xl flex items-center justify-center gap-2 text-white text-[13px] font-bold uppercase tracking-wide disabled:opacity-40"
              style={{ background: "#e31e24" }} data-testid="quote-create-order-btn">
              <ShoppingCart className="h-4 w-4" />
              {readiness?.ready ? "Create Order" : "Resolve items above to create order"}
            </button>
          )}
          <div className="flex gap-2">
            <button onClick={printQuote}
              className="flex-1 h-10 rounded-xl text-[13px] font-semibold flex items-center justify-center gap-1.5"
              style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#475569" }}>
              <Printer className="h-3.5 w-3.5" /> Print
            </button>
            {quote.status === "sent" && (
              <>
                <button onClick={() => updateStatus("accepted")} disabled={statusUpdating}
                  className="flex-1 h-10 rounded-xl text-[13px] font-semibold"
                  style={{ background: "#f0fdf4", color: "#15803d", border: "1px solid #86efac" }}>
                  Accepted
                </button>
                <button onClick={() => updateStatus("rejected")} disabled={statusUpdating}
                  className="flex-1 h-10 rounded-xl text-[13px] font-semibold"
                  style={{ background: "#fef2f2", color: "#991b1b", border: "1px solid #fca5a5" }}>
                  Rejected
                </button>
                <button onClick={() => updateStatus("expired")} disabled={statusUpdating}
                  className="flex-1 h-10 rounded-xl text-[13px] font-semibold"
                  style={{ background: "#fffbeb", color: "#92400e", border: "1px solid #fcd34d" }}>
                  Expired
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {showOrderForm && (
        <CreateOrderConfirm
          quote={quote}
          creating={creatingOrder}
          onCancel={() => setShowOrderForm(false)}
          onConfirm={createOrder}
        />
      )}
    </div>
  );
}

/* ── Order confirmation — payment method, fulfillment, address if
   delivery. Nothing gets created until this is explicitly confirmed. ── */
const PAYMENT_METHODS = [
  { value: "walkin_cash",   label: "Cash" },
  { value: "walkin_card",   label: "Card (in person)" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cod",           label: "Cash on Delivery" },
  { value: "stripe_link",   label: "Stripe Payment Link" },
];

function CreateOrderConfirm({ quote, creating, onCancel, onConfirm }) {
  const [paymentMethod,   setPaymentMethod]   = useState("walkin_cash");
  const [fulfillmentType, setFulfillmentType] = useState("delivery");
  const [shippingCost,    setShippingCost]    = useState(0);
  const [address, setAddress] = useState({ line1: "", suburb: "", state: "", postcode: "", country: "Australia" });

  const canSubmit = fulfillmentType === "click_collect" || (address.line1 && address.suburb && address.state && address.postcode);

  const submit = () => {
    if (!canSubmit) { toast.error("Fill in the full delivery address"); return; }
    onConfirm({ paymentMethod, fulfillmentType, shippingCost, address });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onCancel}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" style={{ border: "1px solid #dde3ef" }} onClick={e => e.stopPropagation()}>
        <div className="p-5 flex items-center justify-between" style={{ borderBottom: "1px solid #eef2f8" }}>
          <div className="font-heading text-lg font-bold" style={{ color: "#0f172a" }}>Confirm Order Details</div>
          <button onClick={onCancel} className="h-8 w-8 flex items-center justify-center rounded-lg" style={{ color: "#94a3b8" }}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4 text-sm">
          <div className="text-[12px] p-2.5 rounded-lg" style={{ background: "#f8fafc", color: "#64748b" }}>
            {quote.customer_name} · {formatAud(quote.subtotal)} · {quote.items.length} item{quote.items.length === 1 ? "" : "s"}
          </div>

          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1.5" style={{ color: "#94a3b8" }}>How did the customer pay?</div>
            <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}
              className="w-full h-10 px-3 rounded-xl text-[13px] outline-none"
              style={{ border: "1px solid #dde3ef", color: "#0f172a" }}
              data-testid="order-confirm-payment-method">
              {PAYMENT_METHODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>

          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1.5" style={{ color: "#94a3b8" }}>Fulfillment</div>
            <div className="flex gap-2">
              <button onClick={() => setFulfillmentType("delivery")}
                className="flex-1 h-10 rounded-xl text-[13px] font-semibold"
                style={{ background: fulfillmentType === "delivery" ? "#1e5bb8" : "#f5f7fb", color: fulfillmentType === "delivery" ? "#fff" : "#64748b" }}>
                Delivery
              </button>
              <button onClick={() => setFulfillmentType("click_collect")}
                className="flex-1 h-10 rounded-xl text-[13px] font-semibold"
                style={{ background: fulfillmentType === "click_collect" ? "#1e5bb8" : "#f5f7fb", color: fulfillmentType === "click_collect" ? "#fff" : "#64748b" }}>
                Click & Collect
              </button>
            </div>
          </div>

          {fulfillmentType === "delivery" && (
            <div className="space-y-2">
              <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>Delivery Address</div>
              <Input value={address.line1} onChange={e => setAddress(a => ({ ...a, line1: e.target.value }))}
                placeholder="Street address" className="h-9 text-[13px] rounded-lg" style={{ borderColor: "#dde3ef" }} />
              <div className="grid grid-cols-2 gap-2">
                <Input value={address.suburb} onChange={e => setAddress(a => ({ ...a, suburb: e.target.value }))}
                  placeholder="Suburb" className="h-9 text-[13px] rounded-lg" style={{ borderColor: "#dde3ef" }} />
                <Input value={address.state} onChange={e => setAddress(a => ({ ...a, state: e.target.value }))}
                  placeholder="State" className="h-9 text-[13px] rounded-lg" style={{ borderColor: "#dde3ef" }} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input value={address.postcode} onChange={e => setAddress(a => ({ ...a, postcode: e.target.value }))}
                  placeholder="Postcode" className="h-9 text-[13px] rounded-lg" style={{ borderColor: "#dde3ef" }} />
                <Input value={shippingCost} onChange={e => setShippingCost(e.target.value)} type="number" step="0.01"
                  placeholder="Shipping cost" className="h-9 text-[13px] rounded-lg font-mono" style={{ borderColor: "#dde3ef" }} />
              </div>
            </div>
          )}
        </div>

        <div className="p-5 flex gap-2" style={{ borderTop: "1px solid #eef2f8" }}>
          <button onClick={onCancel}
            className="flex-1 h-11 rounded-xl text-[13px] font-semibold"
            style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#475569" }}>
            Cancel
          </button>
          <button onClick={submit} disabled={creating}
            className="flex-1 h-11 rounded-xl text-white text-[13px] font-bold uppercase tracking-wide disabled:opacity-50"
            style={{ background: "#e31e24" }} data-testid="order-confirm-submit">
            {creating ? "Creating…" : "Confirm & Create Order"}
          </button>
        </div>
      </div>
    </div>
  );
}